<template>
    <wrapper-features-templates-two-column
    :showLeftColumnSlot="!showFullViewLoader && getCardsList.length"
    :showRightColumnSlot="!showFullViewLoader && getCardsList.length"
    :menuCollapsed="menuCollapsed"
    :showOverlayLoader="showOverlayLoader"
    :showFullViewLoader="showFullViewLoader"
    :showTopRightAreaSlot="!getMobileAppState && !!getCardsList.length && showNewCardButton"
    :tokenTitleFull="t('cards.consult.cardDetails.showSecurityData')"
    :applyFullHeightToRightColumn="!movementsTableData.length && getCardsList.length"
    :applyErrorState="applyCardsErrorState"
    @challengeSuccessHandler="successHandler($event)"
    @challengeErrorHandler="errorHandler($event, requestVirtualCardData)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- We are using this slot, even tho the name is not that clear, it's the position we need for the header -->
        <template v-if="!getMobileAppState && !!getCardsList.length && showNewCardButton" #accounts-card-slot-area>
            <button-primary
            icon="plus"
            buttonSize="medium"
            style="align-self: center;"
            :label="t('cards.consult.addNewCardBtn')"
            @clicked="showNewCardModal = true"/>
        </template>
        <!-- *********************************************
        *                                                *
        *                    Left Column                 *
        *                                                *
        ********************************************** -->
        <template v-if="getCardsList.length" #left-column>
            <div  class="left-column-content" :class="{'mobile': getMobileAppState}">
                <div class="mobile-aligner-helper" :class="{'mobile': getMobileAppState}">
                    <switch-view :isActive="!showCardSlider" @clicked="showCardSlider = !showCardSlider"/>
                    <button-primary
                    v-if="getMobileAppState && showNewCardButton"
                    icon="plus"
                    buttonSize="small"
                    style="align-self: center;"
                    :label="t('cards.consult.addNewCardBtn')"
                    @clicked="showNewCardModal = true"/>
                </div>
                <!-- slider -->
                <cards-slider
                v-if="showCardSlider"
                :selectedCardId="selectedCard.id"
                :cardsList="getCardsList"
                @clicked="updateSelectedCard"/>
                <!-- selector -->
                <dropdown-card
                v-else
                :params="{ withBorder: true }"
                :selectedOption="selectedCard.id"
                :options="getCardsList"
                @clicked="updateSelectedCard"/>
                <!-- Selected Card Info -->
                <transition name="common-enter-animation" mode="out-in" appear>
                    <div class="for-animation-to-work" :key="`selectedCard${selectedCard.id}`">
                        <selected-card-info
                        :selectedCard="selectedCard"
                        :virtualCardData="virtualCardData"
                        @requestVirtualCardData="requestVirtualCardData"/>
                    </div>
                </transition>
            </div>
        </template>
        <!-- *********************************************
        *                                                *
        *                    Right Column                *
        *                                                *
        ********************************************** -->
        <template v-if="getCardsList.length" #right-column>
            <!-- Loader Generic -->
            <loaders-generic v-if="getCardsList.length && showSlot2Loader" fullSize/>
            <!-- *********************************************
            *                  Movements Table               *
            ********************************************** -->
            <transition name="common-enter-animation" mode="out-in">
                <div
                v-if="getCardsList.length && !showSlot2Loader && !applyMovementsErrorState"
                class="right-side-content"
                :class="{'mobile': getMobileAppState}">
                    <!-- Header -->
                    <text-block-header :label="t('cards.consult.leftColumnLabel')" :title="t('cards.consult.leftColumnTitle')"/>
                    <!-- Visible Filters -->
                    <consult-filters-wrapper
                    v-if="showFilters"
                    :filterTags="filterTags"
                    :applyButtonDisabled="applyButtonDisabled"
                    @applyFilters="applyFilters(appliedFilters)"
                    @removeFilterTag="removeFilterTag"
                    @closeAllFilters="closeAllFilters">
                        <!-- Double Calendar -->
                        <input-double-calendar
                        calendarDirection="row"
                        :fromDateParams="beginDateParams"
                        :toDateParams="endDateParams"
                        :dateWindowSize="180"
                        @inputFromDate="appliedFilters.beginDate = $event"
                        @inputToDate="appliedFilters.endDate = $event"/>
                    </consult-filters-wrapper>
                    <!-- Loader Generic -->
                    <loaders-generic v-if="showFiltersLoader"/>
                    <!-- Table -->
                    <table-group-by-date
                    v-if="movementsTableData.length && !showFiltersLoader"
                    :headerData="movementsTableHeaderData"
                    :tableStyles="movementsTableStyles"
                    :tableData="movementsTableData"
                    :hasMore="getMovementsHasMore"
                    :isLoadingMore="tableLoadingMore"
                    @loadMore="loadMoreHandler"
                    @rowClicked="movementsClickHandler($event)"/>
                    <empty-state-generic
                    v-if="!movementsTableData.length && !showFiltersLoader"
                    illustration="emptyState/no-movements"
                    :title="t('cards.consult.emptyState.title')"
                    :description="t('cards.consult.emptyState.description')"/>
                </div>
            </transition>
        </template>
        <!-- *********************************************
        *                                                *
        *               No Cards Empty-State             *
        *                                                *
        ********************************************** -->
        <template v-if="!showFullViewLoader && !showLoader && !getCardsList.length && !applyCardsErrorState" #empty-state-slot>
            <empty-state-generic
            illustration="emptyState/cards-generic"
            :hasButton="showNewCardButton"
            :title="t('cards.consult.emptyState.noCardsTitle')"
            :description="showNewCardButton? t('cards.consult.emptyState.noCardsDescription') : t('cards.consult.emptyState.noCardsDescription2')"
            :buttonLabel="showNewCardButton ? t('cards.consult.emptyState.noCardsBtn') : null"
            @clicked="navigateByName(routeList.CARDS_REQUEST_NAME)"/>
        </template>
    </wrapper-features-templates-two-column>
    <!-- New Card Modal -->
    <new-card-modal v-if="showNewCardModal" @hideMe="showNewCardModal = false"/>
    <!-- Card Movements Details -->
    <modal-detail-mdw-line-group
    v-if="showDetailsModal"
    :title="detailHeaderParams.title"
    :subTitle="detailHeaderParams.subtitle"
    :subTitleIcon="detailHeaderParams.subTitleIcon"
    :requestPayload="detailModalPayload"
    :detailRequestAction="detailActionName"
    :errorStateMessage="t('cards.consult.cardDetails.movementDetailError')"
    :forceNegativeAmount="detailHeaderParams.forceNegativeAmount"
	@hideMe="showDetailsModal = false"/>
    <!-- Error Feedback -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesTwoColumn from '@/components/wrappers/featuresTemplates/TwoColumn'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import SwitchView from '@/components/generic/switch/View'
import CardsSlider from '@/components/generic/sliders/bankCards/Base'
import DropdownCard from '@/components/generic/dropdowns/cards/Card'
import SelectedCardInfo from '@/components/features/cards/SelectedCardInfo'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import TextBlockHeader from '@/components/generic/textBlocks/Header'
import TableGroupByDate from '@/components/generic/table/GroupByDate'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import NewCardModal from '@/components/features/cards/consult/NewCardModal'
import ModalDetailMdwLineGroup from '@/components/generic/modals/side/DetailMdwLineGroup'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Assets */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { getDate } from '@/assets/scripts/dateBuilders'
import { amountFormat } from '@/assets/scripts/amounts'
import { getCurrentRouteMenuItemInfo } from '@/assets/scripts/parsers/menu'
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'
import { clearObject } from '@/assets/scripts/objects'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* Props */
const props = defineProps({
	menuCollapsed: Boolean
})

/* mapActions & mapGetters */
const { requestCardsList, requestCardMovements, requestSecurityInfoForCard, clearFeatureStates } = mapActions(moduleMapper.CARDS_CONSULT)
const { getCardsList, getCardMovements, getLastMovementInfo, getMovementsHasMore } = mapGetters(moduleMapper.CARDS_CONSULT)
const { getMobileAppState } = mapGetters()

/* Loaders consts */
const showOverlayLoader = ref(false)
const showFullViewLoader = ref(false)
const showSlot1Loader = ref(false)
const showSlot2Loader = ref(false)
const showFiltersLoader = ref(false)
const tableLoadingMore = ref(false)
/* ApplyError States */
const applyCardsErrorState = ref(false)
const applyMovementsErrorState = ref(false)
const showNewCardButton = ref(false)
const showNewCardModal = ref(false)

/*************************************************
*                                                *
*                  Card Data                     *
*                                                *
*************************************************/
const showCardSlider = ref(true)
const selectedCard = ref('')
const virtualCardData = ref(null)

const updateSelectedCard = cardId => {
    selectedCard.value = getCardsList.value.find(obj => obj.id === cardId)
    showFilters.value = false
    virtualCardData.value = null
    closeAllFilters()
}

const movementsTableHeaderData = [
	{ label: t('cards.consult.tableHeader.description'), dataAttr: 'description', showInMobile: { groupWith: 'amount' }, rowCellType: 'title' },
	{ label: t('cards.consult.tableHeader.operationDate'), dataAttr: 'operationDate', rowCellType: 'description' },
	{ label: t('cards.consult.tableHeader.amount'), dataAttr: 'amount', rowCellType: 'amount' }
]

const movementsTableStyles = [null, { width: '100px', 'min-width': '100px' }, { 'text-align': 'end' }
]

const movementsTableData = computed(() => {
	return getCardMovements.value.map(movement => {
		return {
			rowDate: movement.transactionDate,
			rowData: [
				{ value: `${movement.transactionId}${movement.orderNumber}${movement.transactionDate}`, dataAttr: 'id' },
				{ type: 'title', value: movement.description, dataAttr: 'description' },
				{
					value: {
						content: getDate(movement.transactionDate),
						classes: 'capitalize'
					},
					dataAttr: 'operationDate'
				},
				{
					type: 'amount',
					value: { content: movement.amount, currency: movement.currency, noBalanceIcon: false, hasArrowIcons: true },
					dataAttr: 'amount'
				}
			]
		}
	})
})

const loadMoreHandler = () => {
	payloadParams.value.pageNumber++
    payloadParams.value.orderNumberPosition = getLastMovementInfo.value.orderNumberPosition
    payloadParams.value.datePosition = getLastMovementInfo.value.datePosition
    requestMovements()
}

/*************************************************
*                                                *
*                   Requests                     *
*                                                *
*************************************************/
const fetchCardsList = async () => {
    showFullViewLoader.value = true
	applyCardsErrorState.value = false

	try {
        await requestCardsList()
        showFullViewLoader.value = false

		if (!getCardsList.value.length) return
        /* Setting Initial Selected Card */
		selectedCard.value = getCardsList.value[0]
		requestMovements()
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyCardsErrorState.value = true
		errorHandler(error.response.data, fetchCardsList)
	}
}

const payloadParams = ref({
    pageNumber: 1,
	size: 20,
    orderNumberPosition: '',
    datePosition: ''
})

const resetPaginationParams = () => {
    payloadParams.value = {
        pageNumber: 1,
        size: 20,
        orderNumberPosition: '',
        datePosition: ''
    }
}

const requestMovements = async () => {
	applyMovementsErrorState.value = false
	showFeedbackToast.value = false

	payloadParams.value.pageNumber > 1 ? tableLoadingMore.value = true : (showFilters.value ? showFiltersLoader.value = true : showSlot2Loader.value = true)
	cancelAllPendingRequests()

	const cardMovementPayload = {
		cardId: selectedCard.value.id,
		params: { ...payloadParams.value, ...appliedFilters.value }
	}

	try {
		await requestCardMovements(cardMovementPayload)
        tableLoadingMore.value = false
        showSlot2Loader.value = false
        showFiltersLoader.value = false
        if (getCardMovements.value.length && selectedCard.value.typeEmitter === 'EMIS') showFilters.value = true
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyMovementsErrorState.value = true
		errorHandler(error.response.data, requestMovements)
	}
}

/*************************************************
*                                                *
*                  Filters Logic                 *
*                                                *
*************************************************/
const showFilters = ref(false)

const appliedFilters = ref({
    beginDate: null,
    endDate: null
})

const filterTags = ref([])

/* Begin Date Params */
const beginDateParams = computed(() => {
    return {
        labelText: t('cards.history.filters.beginDate'),
        placeholder: t('cards.history.placeholders.beginDate'),
        inputValue: appliedFilters.value.beginDate,
        disabledDates: { from: new Date() }
    }
})

/* End Date Params */
const endDateParams = computed(() => {
    return {
        labelText: t('cards.history.filters.endDate'),
        placeholder: t('cards.history.placeholders.endDate'),
        inputValue: appliedFilters.value.endDate,
        disabledDates: {
            from: new Date()
        }
    }
})

const applyButtonDisabled = computed(() => {
    return !appliedFilters.value.beginDate && !appliedFilters.value.endDate
})

const applyFilters = (filters) => {
    appliedFilters.value = filters
    filterTags.value = []

    for (const filter in appliedFilters.value) {
		if (appliedFilters.value[filter]) {
            filterTags.value.push({
                id: filter,
                label: t(`cards.history.filters.${filter}`),
                text: filterTagTextHandler(filter, appliedFilters.value[filter])
            })
        } else {
			appliedFilters.value[filter] = null
		}
	}

    resetPaginationParams()
    requestMovements()
}

const removeFilterTag = (tagId) => {
    appliedFilters.value[tagId] = null
	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []
    resetPaginationParams()
    requestMovements()
}

const closeAllFilters = () => {
    filterTags.value = []
    appliedFilters.value = clearObject(appliedFilters.value)
    resetPaginationParams()
    requestMovements()
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'beginDate':
		case 'endDate':
			return getDate(value)
	}
}

/*************************************************
*                                                *
*                 Details Modal                  *
*                                                *
*************************************************/
const showDetailsModal = ref(false)
const detailHeaderParams = ref(null)
const detailModalPayload = ref(null)
const detailActionName = `${moduleMapper.CARDS_CONSULT}/requestMovimentDetail`

const movementsClickHandler = row => {
	const movementDetail = getCardMovements.value.find(movement => `${movement.transactionId}${movement.orderNumber}${movement.transactionDate}` === row.rowId)

    detailHeaderParams.value = {
        title: movementDetail.description,
        subtitle: amountFormat(Math.abs(movementDetail.amount), movementDetail.currency),
        subTitleIcon: movementDetail.amount > 0 ? 'arrow-up-full' : 'arrow-down-full',
        forceNegativeAmount: movementDetail.amount < 0
    }

    detailModalPayload.value = {
        cardId: selectedCard.value.id,
        params: {
            datePosition: movementDetail.transactionDate,
            orderNumberPosition: movementDetail.orderNumber,
            transactionId: movementDetail.transactionId
        }
    }

	showDetailsModal.value = true
}

const requestVirtualCardData = async () => {
	/* We clear this information so that virtual-card-details component knows to show or hide card data */
	if (virtualCardData.value) {
		virtualCardData.value = null
		return
	}

	showOverlayLoader.value = true

	try {
		await requestSecurityInfoForCard(selectedCard.value.id)
		showOverlayLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, requestMovements)
	}
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
	feedbackToastData.value = sucessObjectConstrutor(data.message)
	virtualCardData.value = data.data
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showFullViewLoader.value = false
    showSlot1Loader.value = false
    showSlot2Loader.value = false
	tableLoadingMore.value = false
    showFiltersLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

fetchCardsList()

onMounted(() => {
    const routeToGetInfo = routeList.CARDS_BASE_PATH
    const menuEntriesForAccelarator = getCurrentRouteMenuItemInfo(routeToGetInfo).children

    if (menuEntriesForAccelarator.find(obj => obj.label === 'cards_request') || menuEntriesForAccelarator.find(obj => obj.label === 'cards_activate')) {
        showNewCardButton.value = !!getCurrentRouteMenuItemInfo('/cards/request')
    }
})

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.left-column-content {
    @include cardGeneral1;
    @include commonEnteringAnimation;

    width: 100%;
    height: auto;
    padding: 16px 0;

    display: flex;
    flex-direction: column;
    align-items: center;

    &.mobile {
        background-color: var(--secondary-light-3);
    }

    .mobile-aligner-helper {
        .switch-wrapper {
            flex-shrink: 0;
        }

        &.mobile {
            width: calc(100% - 32px);
            margin: 16px 0 24px 0;

            display: flex;
            align-items: center;
            justify-content: space-between;

            .switch-wrapper {
                margin-bottom: 0;
            }
        }
    }

    .dropdown-wrapper {
        max-width: calc(100% - 32px);
    }

    .for-animation-to-work {
        @include commonEnteringAnimation;
        width: 100%;
        margin-top: 24px;
    }
}

.right-side-content {
    @include cardGeneral1;
    @include commonEnteringAnimation;

    .date-pickers-wrapper {
        margin-top: 16px;
    }

    &.mobile {
        padding: 16px 0;

        > :deep(*) {
            &:not(.prevent-auto-margins) {
                width: calc(100% - 32px);
                margin-left: auto;
                margin-right: auto;
            }
        }
    }
}
</style>
